import { observer } from 'mobx-react-lite';
import React, { useContext, useEffect, useState } from 'react';
import { Redirect, useParams } from 'react-router';

import { ROUTING_CONFIG, SESSION_STATES, USER_ROLES } from '@uplift/constants';
import { CurrentuserContext } from '@uplift/contexts';
import { trackCloseSessionClick, trackJoinSessionClick, trackRejoinSessionClick, useUpBreakpoint } from '@uplift/hooks';
import { PreVideoCallPage, VideoCallPage, useRoomState, useVideoContext, VideoProvider, HungUpPage } from '@uplift/video-call';
import { Loading } from '@uplift/pages';
import { useTwilioToken } from '@uplift/queries';
import { CalendarDateService } from '@uplift/services';
import { UserRole } from '@uplift/types';
import {
  ContextualNavigationBackButton, ContextualNavigationHeaderProps, ContextualNavigationLayout,
  ContextualNavigationLogOut, UpliftThemeProvider
} from '@uplift/ui-v2';
import { formatUserFullName } from '@uplift/utils';

import { LightLayout, MainLayout } from '../../components';

const AppointmentSessionPageContent: React.VFC<{ appointmentId: string }> = observer(({ appointmentId }) => {
  const isSmUp = useUpBreakpoint('sm');
  const { connect: videoConnect, room, isRoomClosed, localTracks, isConnecting, isLoading, appointment } = useVideoContext();
  const localTracksRef = React.useRef(localTracks);
  const roomState = useRoomState();
  const [ isHungUp, setHungUp ] = useState(appointment?.state === 'completed');

  const me = useContext(CurrentuserContext);

  const { twilioToken, fetching: twilioTokenFetching } = useTwilioToken({ appointmentId: appointmentId });

  const meta =
    appointment?.twilioMeta &&
    JSON.parse(JSON.stringify(appointment?.twilioMeta));

  const availableStates = [
    SESSION_STATES.initial,
    SESSION_STATES.started,
    SESSION_STATES.completed,
    SESSION_STATES.scheduled,
  ];

  const startVideoCall = (closeDialog) => {
    trackJoinSessionClick({
      userType: UserRole.patient
    });
    videoConnect(twilioToken);

    if (closeDialog) {
      setHungUp(false);
    }
  };

  const endVideoCall = () => {
    trackCloseSessionClick({
      userType: UserRole.patient
    });
    setHungUp(true);
    room?.disconnect();
  };

  const rejoinCall = () => {
    trackRejoinSessionClick({
      userType: UserRole.patient
    });
    setHungUp(false);
  };

  ///// Cleanup tracks that may still be on if the user leaves the session route
  React.useEffect( () => {
    localTracksRef.current = localTracks;
  }, [ localTracks ]);

  useEffect(() => {
    return () => {
      localTracksRef.current.forEach( track => {
        track.stop();
      });
    };
  }, []);
  /////
  if (isLoading || twilioTokenFetching || isConnecting) {
    return <Loading />;
  }

  if (isHungUp || appointment?.state === SESSION_STATES.completed || isRoomClosed) {
    return (
      <LightLayout>
        <UpliftThemeProvider>
          <HungUpPage
            me={me}
            appointment={appointment}
            isRoomClosed={appointment?.state === SESSION_STATES.completed || isRoomClosed}
            reEnterCall={rejoinCall}
          />
        </UpliftThemeProvider>
      </LightLayout>
    );
  }

  if (!isConnecting && roomState === 'connected') {
    return (
      <VideoCallPage
        me={me}
        appointment={appointment}
        meta={meta}
        onHungUp={endVideoCall}
      />
    );
  }

  const now = CalendarDateService.date();

  if (
    !appointment
    || now.isAfter(CalendarDateService.date(appointment.toDatetime).add(2, 'hours'))
    || !availableStates.includes(appointment.state)
  ) {
    return <Redirect to={ROUTING_CONFIG.main} />;
  }

  const headerProps: ContextualNavigationHeaderProps = {
    hideLogo: true,
    leftButton: <ContextualNavigationBackButton />,
    rightButton: (
      <ContextualNavigationLogOut name={
        formatUserFullName({
          fallback: me.email,
          firstName: me.firstName,
          lastName: me.lastName,
          preferredFirstName: me.preferredName
        })}
      />
    )
  };

  const content = (
    <PreVideoCallPage
      me={me}
      companionRole={USER_ROLES.provider}
      appointment={appointment}
      onStart={startVideoCall}
      blurAvailable={false}
    />
  );

  return isSmUp ? (
    <MainLayout>
      <UpliftThemeProvider>
        {content}
      </UpliftThemeProvider>
    </MainLayout>
  ) : (
    <ContextualNavigationLayout headerProps={headerProps}>
      {content}
    </ContextualNavigationLayout>
  );
});


export const AppointmentSessionPage = () => {
  const params = useParams<{ appointmentId: string }>();

  return (
    <VideoProvider
      appointmentId={params.appointmentId}
      onError={console.log}
    >
      <AppointmentSessionPageContent
        appointmentId={params.appointmentId}
      />
    </VideoProvider>
  );
};
